// client/src/pages/pmPortal/TenantOverviewPage.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePmPortalApiClient } from '../../hooks/usePmPortalApiClient';
import { PageHeader, Panel, Stat, Table, Loading, Notice, Pill, Field, money, fmtDate, titleCase, statusTone }
    from '../../components/secure/pm/PMKit';

// The rent due day is on the lease; the next occurrence of it is what a resident
// actually wants to know.
const nextDueDate = (dueDay) => {
    if (!dueDay) return null;
    const today = new Date();
    const candidate = new Date(today.getFullYear(), today.getMonth(), dueDay);
    if (candidate < new Date(today.getFullYear(), today.getMonth(), today.getDate())) {
        candidate.setMonth(candidate.getMonth() + 1);
    }
    return candidate.toISOString().slice(0, 10);
};

const TenantOverviewPage = () => {
    const api = usePmPortalApiClient();
    const [leases, setLeases] = useState(null);
    const [ledger, setLedger] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        Promise.all([
            api.get('/api/pm-portal/tenant/leases'),
            api.get('/api/pm-portal/tenant/ledger'),
        ])
            .then(([l, d]) => { setLeases(l); setLedger(d); })
            .catch(e => setError(e.message));
    }, [api]);

    if (error) return <Notice tone="bad" title="Could not load your account" message={error} />;
    if (!leases || !ledger) return <Loading />;

    const active = leases.find(l => ['active', 'holdover', 'month_to_month'].includes(l.status)) || leases[0];
    const overdue = ledger.charges.filter(c => c.outstanding > 0 && c.due_on < new Date().toISOString().slice(0, 10));

    return (
        <>
            <PageHeader
                title={active ? `${active.property_name}${active.unit_number ? ` · Unit ${active.unit_number}` : ''}` : 'Your tenancy'}
                subtitle="Your lease, your charges and your payments, as recorded by the brokerage."
            />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <Stat
                    label="Balance due"
                    value={money(ledger.balance_due)}
                    tone={ledger.balance_due > 0 ? (overdue.length ? 'bad' : 'warn') : 'good'}
                    sub={ledger.balance_due > 0 ? `${overdue.length} item(s) past due` : 'Nothing outstanding'}
                />
                <Stat label="Monthly rent" value={active ? money(active.rent_amount) : '—'} />
                <Stat
                    label="Next rent due"
                    value={active ? fmtDate(nextDueDate(active.rent_due_day)) : '—'}
                    sub={active && active.grace_days ? `${active.grace_days} day grace period` : null}
                />
            </div>

            {overdue.length > 0 && (
                <Notice
                    tone="warn"
                    title="You have an overdue balance"
                    message="Please contact the office to arrange payment. Payments are not taken through this portal."
                />
            )}

            {active && (
                <Panel title="Your lease" actions={<Link to="/pm-portal/tenant/lease" className="text-sm text-indigo-600 hover:underline">Full details</Link>}>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                        <Field label="Status" value={<Pill tone={statusTone(active.status)}>{titleCase(active.status)}</Pill>} />
                        <Field label="Term" value={`${fmtDate(active.start_date)} – ${fmtDate(active.end_date)}`} />
                        <Field label="Address" value={[active.street_address, active.city, active.state, active.zip_code].filter(Boolean).join(', ')} />
                        <Field label="Security deposit held" value={money(active.security_deposit)} hint="Held in a special account, not brokerage funds." />
                    </dl>
                </Panel>
            )}

            <Panel title="Recent charges">
                <Table
                    columns={[
                        { key: 'due_on', label: 'Due', render: r => fmtDate(r.due_on) },
                        { key: 'label', label: 'Charge' },
                        { key: 'amount', label: 'Amount', align: 'right', render: r => money(r.amount) },
                        { key: 'outstanding', label: 'Outstanding', align: 'right', render: r => money(r.outstanding) },
                        { key: 'status', label: 'Status', render: r => <Pill tone={statusTone(r.status)}>{titleCase(r.status)}</Pill> },
                    ]}
                    rows={ledger.charges.slice(0, 8)}
                    rowTone={r => (r.outstanding > 0 ? 'warn' : null)}
                    empty="No charges have been raised yet."
                />
            </Panel>
        </>
    );
};

export default TenantOverviewPage;
